/**
 * webSpaceBgDecorationsDraw.web.ts
 * Web Canvas2D helper that paints the procedural backdrop decoration
 * layer for the World 2 space scene. Split out of
 * `webSpaceDraw.web.ts` so the per-frame paint module stays under the
 * AGENTS.md 300-line cap.
 *
 * Paints each entry's draw recipe (planet body + ring + lit limb,
 * exploded debris cluster, or star pinprick) onto a 2D context. No
 * game-state mutation, no React, no module-level caches.
 *
 * @module webSpaceBgDecorationsDraw
 */

/// <reference lib="dom" />

import type { SpaceBgDecoration } from './spaceBgDecorationTypes';

/**
 * Paint a single procedural backdrop decoration onto `ctx`. Each kind
 * has its own draw recipe (planet body + lit limb, exploded debris
 * cluster, or single star pinprick). Co-located with the per-frame
 * `drawSpaceScene` so the Skia path (`SpaceBgDecorationsLayer.tsx`)
 * and this Canvas2D path stay visually identical — they read the same
 * deterministic builder output.
 *
 * @param ctx - The 2D rendering context.
 * @param d   - The decoration entry to paint.
 * @returns void — paints onto `ctx` as a side effect.
 */
export function drawBgDecoration(
  ctx: CanvasRenderingContext2D,
  d: SpaceBgDecoration,
): void {
  if (d.kind === 'exploded') {
    ctx.fillStyle = d.color;
    const chunks = d.debris ?? [];
    for (const c of chunks) {
      ctx.beginPath();
      ctx.arc(d.cx + c.dx, d.cy + c.dy, c.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
  if (d.kind === 'star') {
    ctx.fillStyle = d.highlight;
    ctx.beginPath();
    ctx.arc(d.cx, d.cy, d.radius, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  // Planet kinds — ring (if present) painted first behind the body,
  // then the body, then the lit limb on top at 35% alpha.
  const ring = d.ring;
  if (ring !== undefined) {
    ctx.strokeStyle = ring.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(d.cx, d.cy, ring.radiusX, ring.radiusY, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = d.color;
  ctx.beginPath();
  ctx.arc(d.cx, d.cy, d.radius, 0, Math.PI * 2);
  ctx.fill();
  const highlightR = Math.max(1, Math.round(d.radius * 0.45));
  const highlightDx = -Math.round(d.radius * 0.3);
  const highlightDy = -Math.round(d.radius * 0.3);
  // DECISION: Restore alpha manually instead of `ctx.save()` /
  // `ctx.restore()` so the decoration pass leaves no trace in the
  // transform stack — the ship-sprite test (and any future stanza
  // that asserts on transformOps ordering) sees only its own save/
  // translate/scale/restore quartet.
  const prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = d.highlight;
  ctx.beginPath();
  ctx.arc(d.cx + highlightDx, d.cy + highlightDy, highlightR, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = prevAlpha;
}
